import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { registerAuthHandlers } from "@/src/api/axiosInstance";
import { isAuthEntryPath } from "@/src/constants/authRoutes";
import { clearAuthError, forceLogout, setSessionTokens } from "@/src/redux/slices/authSlice";
import { useAppDispatch, useAppSelector } from "@/src/redux/hooks";
import { resolvePostAuthDestination } from "@/src/utils/resolvePostAuthDestination";
import { createSignupPropertyDraftIfPresent } from "@/src/utils/signupPropertyDraft";
import { setAccessTokenCookie } from "@/src/utils/storage";

export function AuthBootstrap() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const { accessToken, refreshToken, isAuthenticated, pendingVerification } = useAppSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    registerAuthHandlers({
      getAccessToken: () => accessToken,
      getRefreshToken: () => refreshToken,
      onSessionTokens: (tokens) => {
        dispatch(setSessionTokens(tokens));
      },
      onUnauthorized: () => {
        dispatch(forceLogout());
        if (!isAuthEntryPath(pathname) && pathname !== "/verify-email") {
          window.location.replace("/splash");
        }
      },
    });
  }, [accessToken, dispatch, pathname, refreshToken]);

  useEffect(() => {
    if (!isAuthEntryPath(pathname)) return;
    if (!isAuthenticated) {
      dispatch(clearAuthError());
      if (accessToken) {
        dispatch(forceLogout());
      }
    }
  }, [accessToken, dispatch, isAuthenticated, pathname]);

  useEffect(() => {
    if (accessToken) {
      setAccessTokenCookie(accessToken);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    if (!isAuthEntryPath(pathname) && pathname !== "/verify-email") return;

    let cancelled = false;
    void (async () => {
      await createSignupPropertyDraftIfPresent();
      if (cancelled) return;
      const path = await resolvePostAuthDestination(redirectTo);
      if (!cancelled) {
        navigate(path, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, isAuthenticated, navigate, pathname, redirectTo]);

  useEffect(() => {
    if (isAuthenticated || !pendingVerification) return;
    // Stay on verify / signup / email entry so Back from OTP can reach /login/email.
    if (
      pathname === "/verify-email" ||
      pathname.startsWith("/signup") ||
      pathname === "/login/email"
    ) {
      return;
    }
    if (isAuthEntryPath(pathname) || pathname === "/login") {
      navigate("/verify-email", { replace: true });
    }
  }, [isAuthenticated, pathname, pendingVerification, navigate]);

  return null;
}
